import React, { useState } from 'react';
import { WorkflowBuilder } from '../components/workflow/WorkflowBuilder';

/**
 * Workflows Page Component
 * 
 * Page for managing AI workflows and automation with drag-and-drop builder
 * 
 * @returns {JSX.Element} Workflows page component
 */
export const Workflows: React.FC = () => {
  const [view, setView] = useState<'list' | 'builder'>('builder');
  const [workflows, setWorkflows] = useState([
    {
      id: '1',
      name: 'Email Compliance Workflow',
      description: 'Email Scanner → OCR Agent → Compliance Audit Agent pipeline for automated compliance checking',
      status: 'active',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-20'),
      agents: ['Email Scanner', 'OCR Agent', 'Compliance Audit Agent'],
    },
    {
      id: '2',
      name: 'Document Processing Pipeline',
      description: 'Automated document analysis and classification',
      status: 'active',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-20'),
      agents: ['OCR Agent', 'Content Audit Agent'],
    },
    {
      id: '3',
      name: 'Risk Assessment Workflow',
      description: 'AI-powered risk evaluation and scoring',
      status: 'draft',
      createdAt: new Date('2024-01-18'),
      updatedAt: new Date('2024-01-19'),
      agents: ['Compliance Audit Agent', 'Reporting Agent'],
    },
    {
      id: '4',
      name: 'Customer Support Automation',
      description: 'Automated ticket routing and response generation',
      status: 'paused',
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date('2024-01-17'),
      agents: ['Customer Support Agent', 'AI Translator'],
    },
  ]);

  const handleSaveWorkflow = (workflow: any) => {
    console.log('Saving workflow:', workflow);
    // Here you would typically save to backend
    alert(`Workflow "${workflow.name}" saved successfully!`);
  };

  const handleRunWorkflow = (workflow: any) => {
    console.log('Running workflow:', workflow);
    // Here you would typically trigger workflow execution
    alert(`Workflow "${workflow.name}" execution started!`);
  };

  if (view === 'builder') {
    return (
      <div className="h-full">
        <WorkflowBuilder
          onSave={handleSaveWorkflow}
          onRun={handleRunWorkflow}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Workflows</h1>
            <p className="text-gray-600">Design and manage your AI automation workflows here.</p>
          </div>
          <button
            onClick={() => setView('builder')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create New Workflow
          </button>
        </div>
      </div>

      {/* Workflow List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workflows.map((workflow) => (
          <div
            key={workflow.id}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {workflow.name}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {workflow.description}
                </p>
              </div>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  workflow.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : workflow.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {workflow.status}
              </span>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                <span>Updated {workflow.updatedAt.toLocaleDateString()}</span>
                <span>Created {workflow.createdAt.toLocaleDateString()}</span>
              </div>
              {workflow.agents && (
                <div className="flex flex-wrap gap-1">
                  {workflow.agents.map((agent, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                    >
                      {agent}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setView('builder')}
                className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Edit
              </button>
              <button
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Duplicate
              </button>
              <button
                className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {workflows.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">⚡</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No workflows yet</h3>
          <p className="text-gray-600 mb-6">Create your first AI workflow to get started</p>
          <button
            onClick={() => setView('builder')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Workflow
          </button>
        </div>
      )}
    </div>
  );
};
